import { writesApi } from './api';

// ── Account deletion requests ──────────────────────────────────────────────
// Filed from the three apps (signed in) or the chekit.shop data-deletion page
// (verified by a one-time code). The account is locked while one is pending;
// nothing is erased until someone with users.delete approves it here.
export type DeletionRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface DeletionRequest {
  id: string;
  userId: string;
  source: 'app' | 'web';
  app: 'customer' | 'merchant' | 'rider' | null;
  reason: string | null;
  status: DeletionRequestStatus;
  /** What the account goes back to on a rejection or a cancellation. */
  previousStatus: string;
  requestedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  /** Shown to the holder on rejection. */
  decisionNote: string | null;
  cancelledAt: string | null;
  /** Read live from users, never stored on the request. Anonymised once approved. */
  name?: string;
  phone?: string | null;
  email?: string | null;
  /**
   * Pending only: why approval would be refused right now (an order still
   * running, money in a wallet, on duty, sole business owner), or null when
   * clear. Worded for the account holder, since it is the same sentence
   * they are shown.
   */
  blocker?: string | null;
}

export const deletionStatusChip: Record<DeletionRequestStatus, string> = {
  pending:   'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  approved:  'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  rejected:  'bg-[var(--bg-base)] text-[var(--text-muted)]',
  cancelled: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

// ── Service → Spring Boot :8080 (read: users.read, decide: users.delete) ───
export const deletionRequestsService = {
  /** pending (default, oldest first) | approved | rejected | cancelled (latest 200) */
  list: (status: DeletionRequestStatus = 'pending') =>
    writesApi.get<DeletionRequest[]>('/admin/deletion-requests', { status }),

  get: (id: string) => writesApi.get<DeletionRequest>(`/admin/deletion-requests/${id}`),

  /** Irreversible: re-runs the checks, then anonymises the account. */
  approve: (id: string, note?: string) =>
    writesApi.post<DeletionRequest>(`/admin/deletion-requests/${id}/approve`, { note: note || null }),

  /** Restores the account. The note is required and the holder sees it. */
  reject: (id: string, note: string) =>
    writesApi.post<DeletionRequest>(`/admin/deletion-requests/${id}/reject`, { note }),
};
